import { Link } from "react-router-dom";
import { ArrowClockwise, SignIn } from "@phosphor-icons/react";

/**
 * Calm session-recovery state for teacher / parent / admin pages whose
 * data fetches require a logged-in profile. Shown by `useAuthGate()`
 * when auth has resolved but no profile is present (session expired,
 * token revoked, or auth-context fetch failed).
 *
 * The copy stays human ("just need you to sign in again"), the primary
 * action goes to /login, and a secondary "try again" lets the user
 * retry without a hard navigation if they think the spinner just got
 * stuck. Mobile-safe: stacks vertically and uses comfortable tap
 * targets on small screens.
 */
export default function SessionExpiredState({
  scope = "page",
  testId = "session-expired-state",
}) {
  return (
    <div
      data-testid={testId}
      className="mx-auto w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm sm:p-8"
    >
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 text-neutral-600">
        <SignIn size={20} weight="duotone" aria-hidden />
      </div>
      <h2 className="mt-4 font-heading text-xl font-semibold tracking-tight text-neutral-900">
        Just need you to sign in again
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-neutral-600">
        Your session has timed out. Sign in to keep using the {scope}.
      </p>
      <div className="mt-5 flex flex-col items-stretch gap-2 sm:flex-row sm:justify-center">
        <Link
          to="/login"
          data-testid={`${testId}-signin`}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-hover active:scale-[0.99]"
        >
          <SignIn size={14} weight="bold" />
          Sign in again
        </Link>
        <button
          type="button"
          data-testid={`${testId}-retry`}
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50 active:scale-[0.99]"
        >
          <ArrowClockwise size={14} weight="bold" />
          Try again
        </button>
      </div>
    </div>
  );
}
